﻿import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Stack,
  Dialog,
  DialogContent,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import GenericDialog from '../../Dialog/GenericDialog';
import {
  blobToDownloadableUrl,
  downloadFileNoExtension,
  getFileExtension,
  byteArrayToBlob,
  formatByteArraySize,
  detectFileDataType,
  base64ToBlob,
} from '../../Utilities/HelperMethods';

export default function PdfDownloadWidget({ fileData, fileName, open, onClose }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFetchingFile, setIsFetchingFile] = useState(fileData == null || fileData === undefined);

  async function onDownload() {
    if (fileData == null || fileData === undefined) return;

    const dataType = detectFileDataType(fileData);

    if (dataType === 'byteArray') {
      byteArrayToBlob(fileData, { type: `application/${getFileExtension(fileName)}` }).then(
        (result) => {
          const url = blobToDownloadableUrl(result);
          downloadFileNoExtension(url, fileName);
        }
      );
    } else if (dataType === 'base64') {
      base64ToBlob(fileData, { type: `application/${getFileExtension(fileName)}` }).then(
        (result) => {
          const url = blobToDownloadableUrl(result);
          downloadFileNoExtension(url, fileName);
        }
      );
    }
    //TODO:
    //Show error downloading
  }

  useEffect(() => {
    if (fileData) {
      setIsFetchingFile(false);
    }
  }, [fileData]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownload();
      // Close dialog after successful download
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!isDownloading ? onClose : undefined}
      sx={{
        maxWidth: '400px',
        margin: 'auto',
      }}
    >
      <DialogContent>
        <Stack gap={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.light',
                p: 2,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
          </Box>
          <Stack alignItems="center" textAlign="center">
            <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
              Download PDF
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isFetchingFile
                ? 'Retrieving PDF...'
                : 'Click the button below to download ' + fileName}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            size="md"
            fullWidth
            onClick={handleDownload}
            disabled={isDownloading || isFetchingFile}
            startIcon={
              isDownloading || isFetchingFile ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DownloadIcon />
              )
            }
            sx={{ py: 1.5 }}
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
          <Typography
            textAlign="center"
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, display: 'block' }}
          >
            {(fileData != null || fileData !== undefined) &&
              'File size: ' + formatByteArraySize(fileData)}
          </Typography>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
